import { BaseService } from "../../core/baseService";
import {
  ActivityBookingAdminDetailDto,
  ActivityBookingAdminSummaryDto,
  ActivityAdminDetailDto,
  ActivityAdminServiceInterface,
  ActivityAdminSummaryDto,
  ActivityAddOnAdminDto,
  ActivityCategoryAdminDto,
  ActivityDurationOptionAdminDto,
  ActivityImageUploadResponse,
  ActivityOperatingHoursDto,
  ActivityResourceAdminDto,
  ActivityResourceMappingAdminDto,
  ActivitySpecialHoursDto,
  ActivityWorkerAssignmentAdminDto,
  CreateActivityAddOnDto,
  CreateActivityBookingDto,
  CreateActivityCategoryDto,
  CreateActivityDto,
  CreateActivityDurationOptionDto,
  CreateActivityResourceDto,
  CreateActivityResourceMappingDto,
  CreateActivitySpecialHoursDto,
  CreateActivityWorkerAssignmentDto,
  GetActivityBookingsParams,
  GetActivitiesParams,
  UpdateActivityBookingDto,
  UpdateActivityBookingStatusDto,
  UpdateActivityAddOnDto,
  UpdateActivityCategoryDto,
  UpdateActivityDto,
  UpdateActivityDurationOptionDto,
  UpdateActivityOperatingHoursDto,
  UpdateActivityResourceDto,
  UpdateActivityResourceMappingDto,
  UpdateActivitySpecialHoursDto,
  UpdateActivityWorkerAssignmentDto,
} from "../types/activityAdmin.types";

export class ActivityAdminService extends BaseService implements ActivityAdminServiceInterface {
  async getCategories(includeInactive = false) {
    return this.handleRequest<ActivityCategoryAdminDto[]>(
      this.client.get("/api/admin/activities/categories", {
        params: this.buildQueryParams({ includeInactive }),
      }),
    );
  }

  async getCategoryById(categoryId: string) {
    return this.handleRequest<ActivityCategoryAdminDto>(
      this.client.get(`/api/admin/activities/categories/${categoryId}`),
    );
  }

  async createCategory(dto: CreateActivityCategoryDto) {
    return this.handleRequest<ActivityCategoryAdminDto>(
      this.client.post("/api/admin/activities/categories", dto),
    );
  }

  async updateCategory(categoryId: string, dto: UpdateActivityCategoryDto) {
    return this.handleRequest<void>(
      this.client.put(`/api/admin/activities/categories/${categoryId}`, dto),
    );
  }

  async deleteCategory(categoryId: string) {
    return this.handleRequest<void>(
      this.client.delete(`/api/admin/activities/categories/${categoryId}`),
    );
  }

  async getResources(includeInactive = false) {
    return this.handleRequest<ActivityResourceAdminDto[]>(
      this.client.get("/api/admin/activities/resources", {
        params: this.buildQueryParams({ includeInactive }),
      }),
    );
  }

  async getResourceById(resourceId: string) {
    return this.handleRequest<ActivityResourceAdminDto>(
      this.client.get(`/api/admin/activities/resources/${resourceId}`),
    );
  }

  async createResource(dto: CreateActivityResourceDto) {
    return this.handleRequest<ActivityResourceAdminDto>(
      this.client.post("/api/admin/activities/resources", dto),
    );
  }

  async updateResource(resourceId: string, dto: UpdateActivityResourceDto) {
    return this.handleRequest<void>(
      this.client.put(`/api/admin/activities/resources/${resourceId}`, dto),
    );
  }

  async deleteResource(resourceId: string) {
    return this.handleRequest<void>(
      this.client.delete(`/api/admin/activities/resources/${resourceId}`),
    );
  }

  async getActivities(params: GetActivitiesParams = {}) {
    return this.handleRequest<ActivityAdminSummaryDto[]>(
      this.client.get("/api/admin/activities", {
        params: this.buildQueryParams({
          includeInactive: params.includeInactive,
          categoryId: params.categoryId,
        }),
      }),
    );
  }

  async getActivityById(activityId: string) {
    return this.handleRequest<ActivityAdminDetailDto>(
      this.client.get(`/api/admin/activities/${activityId}`),
    );
  }

  async createActivity(dto: CreateActivityDto) {
    return this.handleRequest<ActivityAdminDetailDto>(
      this.client.post("/api/admin/activities", dto),
    );
  }

  async updateActivity(activityId: string, dto: UpdateActivityDto) {
    return this.handleRequest<void>(
      this.client.put(`/api/admin/activities/${activityId}`, dto),
    );
  }

  async deleteActivity(activityId: string) {
    return this.handleRequest<void>(
      this.client.delete(`/api/admin/activities/${activityId}`),
    );
  }

  async uploadActivityImage(activityId: string, image: File) {
    const formData = new FormData();
    formData.append("file", image);

    return this.handleRequest<ActivityImageUploadResponse>(
      this.client.post(`/api/admin/activities/${activityId}/image`, formData, {
        transformRequest: [
          (data, headers) => {
            delete headers["Content-Type"];
            return data;
          },
        ],
      }),
    );
  }

  async removeActivityImage(activityId: string) {
    return this.handleRequest<void>(
      this.client.delete(`/api/admin/activities/${activityId}/image`),
    );
  }

  async getDurationOptions(activityId: string, includeInactive = false) {
    return this.handleRequest<ActivityDurationOptionAdminDto[]>(
      this.client.get(`/api/admin/activities/${activityId}/duration-options`, {
        params: this.buildQueryParams({ includeInactive }),
      }),
    );
  }

  async createDurationOption(activityId: string, dto: CreateActivityDurationOptionDto) {
    return this.handleRequest<ActivityDurationOptionAdminDto>(
      this.client.post(`/api/admin/activities/${activityId}/duration-options`, dto),
    );
  }

  async updateDurationOption(activityId: string, optionId: string, dto: UpdateActivityDurationOptionDto) {
    return this.handleRequest<void>(
      this.client.put(`/api/admin/activities/${activityId}/duration-options/${optionId}`, dto),
    );
  }

  async deleteDurationOption(activityId: string, optionId: string) {
    return this.handleRequest<void>(
      this.client.delete(`/api/admin/activities/${activityId}/duration-options/${optionId}`),
    );
  }

  async getAddOns(activityId: string, includeInactive = false) {
    return this.handleRequest<ActivityAddOnAdminDto[]>(
      this.client.get(`/api/admin/activities/${activityId}/add-ons`, {
        params: this.buildQueryParams({ includeInactive }),
      }),
    );
  }

  async createAddOn(activityId: string, dto: CreateActivityAddOnDto) {
    return this.handleRequest<ActivityAddOnAdminDto>(
      this.client.post(`/api/admin/activities/${activityId}/add-ons`, dto),
    );
  }

  async updateAddOn(activityId: string, addOnId: string, dto: UpdateActivityAddOnDto) {
    return this.handleRequest<void>(
      this.client.put(`/api/admin/activities/${activityId}/add-ons/${addOnId}`, dto),
    );
  }

  async deleteAddOn(activityId: string, addOnId: string) {
    return this.handleRequest<void>(
      this.client.delete(`/api/admin/activities/${activityId}/add-ons/${addOnId}`),
    );
  }

  async getOperatingHours(activityId: string) {
    return this.handleRequest<ActivityOperatingHoursDto[]>(
      this.client.get(`/api/admin/activities/${activityId}/operating-hours`),
    );
  }

  async replaceOperatingHours(activityId: string, dto: UpdateActivityOperatingHoursDto) {
    return this.handleRequest<ActivityOperatingHoursDto[]>(
      this.client.put(`/api/admin/activities/${activityId}/operating-hours`, dto),
    );
  }

  async getSpecialHours(activityId: string) {
    return this.handleRequest<ActivitySpecialHoursDto[]>(
      this.client.get(`/api/admin/activities/${activityId}/special-hours`),
    );
  }

  async createSpecialHours(activityId: string, dto: CreateActivitySpecialHoursDto) {
    return this.handleRequest<ActivitySpecialHoursDto>(
      this.client.post(`/api/admin/activities/${activityId}/special-hours`, dto),
    );
  }

  async updateSpecialHours(activityId: string, specialHoursId: string, dto: UpdateActivitySpecialHoursDto) {
    return this.handleRequest<void>(
      this.client.put(`/api/admin/activities/${activityId}/special-hours/${specialHoursId}`, dto),
    );
  }

  async deleteSpecialHours(activityId: string, specialHoursId: string) {
    return this.handleRequest<void>(
      this.client.delete(`/api/admin/activities/${activityId}/special-hours/${specialHoursId}`),
    );
  }

  async getWorkerAssignments(activityId: string, includeInactive = false) {
    return this.handleRequest<ActivityWorkerAssignmentAdminDto[]>(
      this.client.get(`/api/admin/activities/${activityId}/worker-assignments`, {
        params: this.buildQueryParams({ includeInactive }),
      }),
    );
  }

  async createWorkerAssignment(activityId: string, dto: CreateActivityWorkerAssignmentDto) {
    return this.handleRequest<ActivityWorkerAssignmentAdminDto>(
      this.client.post(`/api/admin/activities/${activityId}/worker-assignments`, dto),
    );
  }

  async updateWorkerAssignment(activityId: string, assignmentId: string, dto: UpdateActivityWorkerAssignmentDto) {
    return this.handleRequest<void>(
      this.client.put(`/api/admin/activities/${activityId}/worker-assignments/${assignmentId}`, dto),
    );
  }

  async deleteWorkerAssignment(activityId: string, assignmentId: string) {
    return this.handleRequest<void>(
      this.client.delete(`/api/admin/activities/${activityId}/worker-assignments/${assignmentId}`),
    );
  }

  async getResourceMappings(activityId: string) {
    return this.handleRequest<ActivityResourceMappingAdminDto[]>(
      this.client.get(`/api/admin/activities/${activityId}/resource-mappings`),
    );
  }

  async createResourceMapping(activityId: string, dto: CreateActivityResourceMappingDto) {
    return this.handleRequest<ActivityResourceMappingAdminDto>(
      this.client.post(`/api/admin/activities/${activityId}/resource-mappings`, dto),
    );
  }

  async updateResourceMapping(activityId: string, mappingId: string, dto: UpdateActivityResourceMappingDto) {
    return this.handleRequest<void>(
      this.client.put(`/api/admin/activities/${activityId}/resource-mappings/${mappingId}`, dto),
    );
  }

  async deleteResourceMapping(activityId: string, mappingId: string) {
    return this.handleRequest<void>(
      this.client.delete(`/api/admin/activities/${activityId}/resource-mappings/${mappingId}`),
    );
  }

  async getBookings(params: GetActivityBookingsParams = {}) {
    return this.handleRequest<ActivityBookingAdminSummaryDto[]>(
      this.client.get("/api/admin/activities/bookings", {
        params: this.buildQueryParams({
          activityId: params.activityId,
          status: params.status,
          fromUtc: params.fromUtc,
          toUtc: params.toUtc,
          includePast: params.includePast,
        }),
      }),
    );
  }

  async getPendingBookingCount() {
    return this.handleRequest<{ count: number }>(
      this.client.get("/api/admin/activities/bookings/pending-count"),
    );
  }

  async getBookingById(bookingId: string) {
    return this.handleRequest<ActivityBookingAdminDetailDto>(
      this.client.get(`/api/admin/activities/bookings/${bookingId}`),
    );
  }

  async createBooking(dto: CreateActivityBookingDto) {
    return this.handleRequest<ActivityBookingAdminDetailDto>(
      this.client.post("/api/admin/activities/bookings", dto),
    );
  }

  async updateBooking(bookingId: string, dto: UpdateActivityBookingDto) {
    return this.handleRequest<void>(
      this.client.put(`/api/admin/activities/bookings/${bookingId}`, dto),
    );
  }

  async updateBookingStatus(bookingId: string, dto: UpdateActivityBookingStatusDto) {
    return this.handleRequest<ActivityBookingAdminDetailDto>(
      this.client.put(`/api/admin/activities/bookings/${bookingId}/status`, dto),
    );
  }

  async cancelBooking(bookingId: string, dto?: Omit<UpdateActivityBookingStatusDto, "status">) {
    return this.handleRequest<ActivityBookingAdminDetailDto>(
      this.client.put(`/api/admin/activities/bookings/${bookingId}/cancel`, dto ?? {}),
    );
  }
}
